import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, RefreshCw, Calendar, Download, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'N/A';
  return new Intl.NumberFormat('es-VE', {
    style: 'currency', currency: 'VES', minimumFractionDigits: 2,
  }).format(amount);
};

/**
 * Formatea fecha + hora de Banco Plaza preservando la hora local VE.
 *
 * Banco Plaza puede devolver fechas en VARIOS formatos:
 *   1) fecha="2026-05-05",            hora="143005"     → ensamblar
 *   2) fecha="2026-05-05",            hora="14:30:05"   → ensamblar
 *   3) fecha="2026-05-05T14:30:05",   hora=null         → usar parte de hora de fecha
 *   4) fecha="2026-05-05T14:30:05Z",  hora=null         → idem (UTC, NO convertir)
 *   5) fecha="2026-05-05 14:30:05",   hora=null         → idem
 *   6) fecha="05/05/2026",            hora=...          → normalizar fecha primero
 *   7) fecha=null/undefined                              → 'N/A'
 *
 * Bug histórico: new Date("2026-05-05") = 2026-05-05T00:00:00Z, que en TZ Caracas
 * (UTC-4) renderiza como "4 de may de 2026, 20:00:00" mostrando el día anterior.
 * Por eso renderizamos sin convertir TZ — los datos del banco YA están en hora VE.
 */
const formatBankDateTime = (fecha, hora) => {
  try {
    if (!fecha) return 'N/A';

    // Si llega un objeto Date de JS, convertir a ISO
    let dateInput = fecha;
    if (fecha instanceof Date) {
      dateInput = fecha.toISOString();
    } else if (typeof fecha === 'object' && fecha !== null) {
      // Objeto inesperado (raro): mostrar JSON resumido
      return JSON.stringify(fecha).slice(0, 30);
    }

    let dateStr = String(dateInput).trim();
    let timeFromDate = '';

    // Caso ISO o "YYYY-MM-DD HH:MM:SS" → separar fecha y hora
    const tsMatch = dateStr.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2})?)/);
    if (tsMatch) {
      dateStr = tsMatch[1];
      timeFromDate = tsMatch[2];
    } else if (dateStr.includes('T')) {
      dateStr = dateStr.split('T')[0];
    }

    // DD/MM/YYYY → YYYY-MM-DD
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3 && parts[2].length === 4) {
        dateStr = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return `${String(fecha).slice(0, 25)}${hora ? ' ' + String(hora).slice(0, 8) : ''}`;
    }

    let timeStr = '';
    let timeCandidate = (hora && String(hora).trim()) || timeFromDate;
    if (timeCandidate) {
      const h = String(timeCandidate).trim();
      if (/^\d{6}$/.test(h)) {
        timeStr = `${h.slice(0, 2)}:${h.slice(2, 4)}:${h.slice(4, 6)}`;
      } else if (/^\d{4}$/.test(h)) {
        timeStr = `${h.slice(0, 2)}:${h.slice(2, 4)}:00`;
      } else if (/^\d{2}:\d{2}(:\d{2})?$/.test(h)) {
        timeStr = h.length === 5 ? `${h}:00` : h;
      }
    }

    const [y, m, d] = dateStr.split('-');
    const datePart = `${d}/${m}/${y}`;
    if (timeStr) {
      const hhmm = timeStr.slice(0, 5);
      return `${datePart} ${hhmm}`;
    }
    return datePart;
  } catch (err) {
    // Nunca dejar que un fallo de formato crashee el componente padre.
    // eslint-disable-next-line no-console
    console.warn('formatBankDateTime failed:', err, { fecha, hora });
    return `[fmt error: ${String(fecha).slice(0, 25)}]`;
  }
};

const NaturalezaBadge = ({ n }) => {
  const isCR = n === 'CR' || n === 'C';
  const cls = isCR ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-700';
  return <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${cls}`}>{isCR ? 'CR' : 'DB'}</span>;
};

export default function AccountMovementsModal({ account, accountLabel, idCliente, onClose }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [useCustom, setUseCustom] = useState(false);
  const [customRange, setCustomRange] = useState({ from: '', to: '' });

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const params = { account };
      if (useCustom && customRange.from && customRange.to) {
        params.fecha_inicio = customRange.from;
        params.fecha_fin = customRange.to;
      } else {
        params.days = days;
      }
      if (idCliente) params.id_cliente = idCliente;
      const res = await axios.get(`${BACKEND_URL}/api/super-admin/bank-accounts/movements`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setData(res.data);
      if (res.data.error) setError(res.data.error);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // En modo custom, sólo recargar cuando ambas fechas estén presentes.
    if (useCustom && (!customRange.from || !customRange.to)) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, account, idCliente, useCustom, customRange.from, customRange.to]);

  // Defender contra response.data inesperado (p.ej. el banco devuelve un dict
  // en vez de array, o nada). Forzamos a array siempre.
  const rawMovements = data?.movements;
  const movements = Array.isArray(rawMovements)
    ? rawMovements
    : (rawMovements && typeof rawMovements === 'object' ? Object.values(rawMovements) : []);

  const safeStr = (v) => (v === null || v === undefined ? '' : String(v));
  /** Banco Plaza devuelve la descripción como `concepto`, no `descripcion`.
   *  Soportamos ambos por compatibilidad. */
  const getDescription = (m) => safeStr(m.concepto || m.descripcion);
  /** El "ordenante" no es un campo del banco — usualmente está embebido
   *  al final del concepto (ej: "TDY PAGO DE PLAZA 4142899874"). */
  const getOrdenante = (m) => safeStr(m.ordenante);

  const filtered = movements.filter(m => {
    if (!m || typeof m !== 'object') return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      getDescription(m).toLowerCase().includes(q)
      || safeStr(m.referencia).toLowerCase().includes(q)
      || safeStr(m.monto).includes(q)
    );
  });

  const totalCR = movements
    .filter(m => m && (m.naturaleza === 'CR' || m.naturaleza === 'C'))
    .reduce((s, m) => s + Number(m.monto || 0), 0);
  const totalDB = movements
    .filter(m => m && !['CR', 'C'].includes(m.naturaleza))
    .reduce((s, m) => s + Number(m.monto || 0), 0);

  const exportCSV = () => {
    const header = ['Fecha', 'Hora', 'Naturaleza', 'Monto', 'Referencia', 'Concepto', 'Tipo'];
    const rows = movements.map(m => [
      safeStr(m.fecha), safeStr(m.hora), safeStr(m.naturaleza), safeStr(m.monto),
      safeStr(m.referencia), getDescription(m).replace(/[\r\n,]/g, ' '),
      safeStr(m.tipo),
    ]);
    const csv = [header, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `movimientos_${account}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="account-movements-modal">
      <div className="bg-white rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ backgroundColor: '#051C2C' }}>
          <div className="flex items-center gap-3 text-white">
            <Calendar className="w-5 h-5" />
            <div>
              <h3 className="text-lg font-bold">Movimientos bancarios</h3>
              <p className="text-xs text-blue-200">
                {accountLabel} · <span className="font-mono">{account}</span>
                {idCliente && <span className="ml-2 px-1.5 py-0.5 bg-orange-500/30 rounded text-[10px]">RIF {idCliente}</span>}
              </p>
            </div>
          </div>
          <button onClick={onClose} data-testid="movements-modal-close" className="text-white hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border bg-white p-4">
              <div className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide">Saldo actual</div>
              <div className="text-xl font-bold text-slate-900 mt-1">{formatCurrency(data?.balance)}</div>
            </div>
            <div className="rounded-xl border bg-white p-4">
              <div className="text-[10px] uppercase text-emerald-600 font-semibold tracking-wide">Créditos (CR)</div>
              <div className="text-xl font-bold text-emerald-700 mt-1">{formatCurrency(totalCR)}</div>
            </div>
            <div className="rounded-xl border bg-white p-4">
              <div className="text-[10px] uppercase text-red-600 font-semibold tracking-wide">Débitos (DB)</div>
              <div className="text-xl font-bold text-red-700 mt-1">{formatCurrency(totalDB)}</div>
            </div>
            <div className="rounded-xl border bg-white p-4">
              <div className="text-[10px] uppercase text-slate-500 font-semibold tracking-wide">Movimientos</div>
              <div className="text-xl font-bold text-slate-900 mt-1">{movements.length}</div>
            </div>
          </div>

          {/* Controls */}
          <div className="rounded-xl border bg-white p-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs uppercase text-slate-500 font-semibold tracking-wide">Período</span>
              {[1, 7, 15, 30, 90, 180, 365].map(d => (
                <button
                  key={d}
                  onClick={() => { setUseCustom(false); setDays(d); }}
                  data-testid={`movements-period-${d}`}
                  className={`px-3 py-1 rounded-lg text-xs font-medium border ${!useCustom && days === d ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                >
                  {d === 1 ? 'Hoy' : d >= 365 ? '1 año' : d >= 30 ? `${Math.round(d/30)}m` : `${d}d`}
                </button>
              ))}
              <button
                onClick={() => setUseCustom(v => !v)}
                data-testid="movements-period-custom"
                className={`px-3 py-1 rounded-lg text-xs font-medium border ${useCustom ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              >
                Custom
              </button>
              <div className="relative ml-auto">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  data-testid="movements-search"
                  className="pl-7 pr-3 py-1 border rounded-lg text-sm w-48"
                />
              </div>
              <button onClick={load} data-testid="movements-refresh" className="text-xs flex items-center gap-1 text-slate-600 hover:text-slate-900 px-2 py-1">
                <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refrescar
              </button>
              <button onClick={exportCSV} disabled={!movements.length} data-testid="movements-export-csv" className="text-xs flex items-center gap-1 text-slate-600 hover:text-slate-900 disabled:opacity-50 px-2 py-1">
                <Download className="w-3 h-3" /> CSV
              </button>
            </div>
            {useCustom && (
              <div className="flex items-center gap-2 pt-2 border-t">
                <span className="text-xs text-slate-500">Desde:</span>
                <input
                  type="date"
                  value={customRange.from}
                  onChange={e => setCustomRange(r => ({ ...r, from: e.target.value }))}
                  data-testid="movements-custom-from"
                  className="px-2 py-1 border rounded text-sm"
                />
                <span className="text-xs text-slate-500">Hasta:</span>
                <input
                  type="date"
                  value={customRange.to}
                  onChange={e => setCustomRange(r => ({ ...r, to: e.target.value }))}
                  data-testid="movements-custom-to"
                  className="px-2 py-1 border rounded text-sm"
                />
                <span className="text-[10px] text-slate-400 ml-auto">Banco Plaza expone máximo ~90 días vía API. Para histórico más antiguo consulta el extracto bancario.</span>
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Movements table */}
          <div className="rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2 text-left">Fecha</th>
                  <th className="px-3 py-2 text-left">Tipo</th>
                  <th className="px-3 py-2 text-right">Monto</th>
                  <th className="px-3 py-2 text-left">Referencia</th>
                  <th className="px-3 py-2 text-left">Concepto</th>
                  <th className="px-3 py-2 text-left">Cód.</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.length === 0 && !loading && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-slate-500">
                    Sin movimientos en este período. {idCliente ? '' : 'Si esta es una concentradora propia, asegurate de pasar el RIF.'}
                  </td></tr>
                )}
                {filtered.map((m, idx) => {
                  const isCR = m.naturaleza === 'CR' || m.naturaleza === 'C';
                  const rawDateInfo = `RAW: fecha=${JSON.stringify(m.fecha)} hora=${JSON.stringify(m.hora)}`;
                  const monto = Number(m.monto || 0);
                  const concepto = getDescription(m);
                  return (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-xs whitespace-nowrap font-mono" title={rawDateInfo}>
                        {formatBankDateTime(m.fecha, m.hora)}
                      </td>
                      <td className="px-3 py-2"><NaturalezaBadge n={m.naturaleza} /></td>
                      <td className={`px-3 py-2 text-right font-mono font-semibold ${isCR ? 'text-emerald-700' : 'text-red-700'}`}>
                        {isCR ? '+' : '-'}{formatCurrency(Math.abs(monto))}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{safeStr(m.referencia) || '-'}</td>
                      <td className="px-3 py-2 text-xs max-w-md truncate" title={concepto}>{concepto || '-'}</td>
                      <td className="px-3 py-2 text-[10px] text-slate-500 font-mono">{safeStr(m.tipo) || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {data?.queried_with_rif && (
            <div className="text-[10px] text-slate-500 font-mono flex items-center justify-between">
              <span>✓ Consulta hecha con id_cliente={String(data.queried_with_rif)} (concentradora propia)</span>
              <button
                onClick={() => {
                  try {
                    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
                  } catch (e) { /* noop */ }
                }}
                data-testid="movements-copy-raw"
                className="px-2 py-0.5 text-[10px] border rounded hover:bg-white"
                title="Copiar respuesta cruda del backend (debug)"
              >
                Copiar raw JSON
              </button>
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t bg-slate-50 flex justify-end">
          <button onClick={onClose} className="px-4 py-1.5 text-sm border rounded-lg hover:bg-white">Cerrar</button>
        </div>
      </div>
    </div>
  );
}
